import { useCallback, useEffect, useMemo } from 'react';
import { useChatStoreInput } from '@/features/chats/stores';
import type { UseOptionsManagerConfig } from './useOptionsManager.types';

/**
 * Manages options state for chat option components with preservation across message sends.
 * Handles initialization from chat store, synchronization with parent component,
 * and provides a consistent update interface.
 */
export function useOptionsManager<TOptions extends Record<string, unknown>>(
  params: UseOptionsManagerConfig<TOptions>,
) {
  const { defaultOptions, onOptionUpdated, toStore, fromStore } = params;
  const { chatInput } = useChatStoreInput();

  const options = useMemo(() => {
    const storedOptions = chatInput.options as Partial<TOptions> | undefined;
    if (fromStore) {
      return fromStore(storedOptions, defaultOptions);
    }
    return {
      ...defaultOptions,
      ...storedOptions,
    } as TOptions;
  }, [chatInput.options, defaultOptions, fromStore]);

  /**
   * Apply toStore transformation to options if provided
   */
  const applyTransform = useCallback(
    (opts: TOptions) => (toStore ? toStore(opts) : opts) as TOptions,
    [toStore],
  );

  // Initialize options on mount to ensure backend receives valid defaults.
  // Without this, chatInput.options remains undefined and the backend uses
  // its own defaults which may not match the UI's.
  useEffect(() => {
    if (!chatInput.options) {
      onOptionUpdated(applyTransform(options));
    }
    // Intentionally empty - only initialize once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Bulk update options with partial updates or updater function
   */
  const setOptions = useCallback(
    (updates: Partial<TOptions> | ((prev: TOptions) => TOptions)) => {
      onOptionUpdated((prev) => {
        // What the popup shows, not the raw store: the store can hold other keys (the chat model)
        // before any of these options, and an update must build on the options as shown.
        const currentOptions = fromStore
          ? fromStore(prev as Partial<TOptions> | undefined, defaultOptions)
          : ((prev ?? defaultOptions) as TOptions);
        const next =
          typeof updates === 'function'
            ? updates(currentOptions)
            : {
                ...currentOptions,
                ...updates,
              };
        return applyTransform(next);
      });
    },
    [onOptionUpdated, applyTransform, defaultOptions, fromStore],
  );

  return {
    options,
    setOptions,
  };
}
